/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 05 2024
 **/

import { useState } from "react";
import { Menu, Radio, useToast } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";

import * as S from "./private/emotion";
import { usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID } from "@/frameworks/core/commons";
import { useChatRoomActions } from "@/frameworks/core/chat";

type Props = {
	roomID: string;
	currentOption: AlarmOptions;
	popoverID: GROUP_POPOVER_ID;
	onClick?: (e: React.MouseEvent<HTMLLIElement>) => void;
};

interface GeneralChatAlarmSettingProps extends Props {
	isGeneral: true;
}

interface OtherChatAlarmSettingProps extends Props {
	isGeneral: false;
}

type AlarmSettingProps = GeneralChatAlarmSettingProps | OtherChatAlarmSettingProps;

function isGeneral(props: AlarmSettingProps): props is GeneralChatAlarmSettingProps {
	return props.isGeneral;
}

export default function ChatAlarmSetting(props: AlarmSettingProps) {
	if (isGeneral(props)) {
		return <GeneralChatAlarmSetting {...props} />;
	} else {
		return <OtherChatAlarmSetting {...props} />;
	}
}

export function OtherChatAlarmSetting({ roomID, currentOption, popoverID }: OtherChatAlarmSettingProps) {
	const [alarmOption, setAlarmOption] = useState<AlarmOptions>(currentOption);
	const toast = useToast();
	const popover = usePopover();
	const chatRoomActions = useChatRoomActions();

	const handleChangeOption = (value: AlarmOptions) => {
		setAlarmOption(value);
		chatRoomActions.common.updateNotification({ roomID, notificationType: value }).then(() => {
			switch (value) {
				case "ALL": {
					toast.show({ type: "system", message: _T("전체 메시지에 대한 알림이 전송됩니다.") });
					break;
				}
				case "ONLY_MENTION": {
					toast.show({ type: "system", message: _T("나를 멘션한 경우에만 알림이 전송됩니다.") });
					break;
				}
				case "OFF": {
					toast.show({ type: "system", message: _T("메시지 알림이 전송되지 않습니다.") });
					break;
				}
			}
			popover.hide(popoverID);
		});
	};

	return (
		<S.SubMenu fullWidth>
			<Menu.List>
				<S.MenuItem
					endEnhancer={
						<Radio
							readOnly
							onClick={() => handleChangeOption("ALL")}
							value="ALL"
							checked={alarmOption === "ALL"}
						/>
					}
				>
					{_T("모든 메시지")}
				</S.MenuItem>
				<S.MenuItem
					endEnhancer={
						<Radio
							readOnly
							onClick={() => handleChangeOption("ONLY_MENTION")}
							value={"ONLY_MENTION"}
							checked={alarmOption === "ONLY_MENTION"}
						/>
					}
				>
					{_T("@멘션만")}
				</S.MenuItem>
				<S.MenuItem
					endEnhancer={
						<Radio
							readOnly
							onClick={() => handleChangeOption("OFF")}
							value="OFF"
							checked={alarmOption === "OFF"}
						/>
					}
				>
					{_T("알림 끄기")}
				</S.MenuItem>
			</Menu.List>
		</S.SubMenu>
	);
}

function GeneralChatAlarmSetting({ roomID, currentOption, popoverID }: GeneralChatAlarmSettingProps) {
	const [alarmOption, setAlarmOption] = useState<AlarmOptions>(currentOption);
	const toast = useToast();
	const popover = usePopover();
	const chatRoomActions = useChatRoomActions();

	const handleChangeOption = (value: AlarmOptions) => {
		setAlarmOption(value);
		chatRoomActions.common.updateNotification({ roomID, notificationType: value }).then(() => {
			switch (value) {
				case "ALL": {
					toast.show({ type: "system", message: _T("전체 메시지에 대한 알림이 전송됩니다.") });
					break;
				}
				case "ONLY_MENTION": {
					toast.show({ type: "system", message: _T("나를 멘션한 경우에만 알림이 전송됩니다.") });
					break;
				}
				case "OFF": {
					toast.show({ type: "system", message: _T("메시지 알림이 전송되지 않습니다.") });
					break;
				}
			}
			popover.hide(popoverID);
		});
	};

	return (
		<S.SubMenu fullWidth>
			<Menu.List>
				<S.MenuItem
					endEnhancer={
						<Radio
							readOnly
							onClick={() => handleChangeOption("ALL")}
							value="ALL"
							checked={alarmOption === "ALL"}
						/>
					}
				>
					{_T("모든 메시지")}
				</S.MenuItem>
				<S.MenuItem
					endEnhancer={
						<Radio
							readOnly
							onClick={() => handleChangeOption("ONLY_MENTION")}
							value="ONLY_MENTION"
							checked={alarmOption === "ONLY_MENTION"}
						/>
					}
				>
					{_T("@멘션만")}
				</S.MenuItem>
				<S.MenuItem
					endEnhancer={
						<Radio
							readOnly
							onClick={() => handleChangeOption("OFF")}
							value="OFF"
							checked={alarmOption === "OFF"}
						/>
					}
				>
					{_T("알림 끄기")}
				</S.MenuItem>
			</Menu.List>
		</S.SubMenu>
	);
}
